import { useQuery } from '@tanstack/react-query';
import { platformApi } from '../api/endpoints/platform';
import type { PrincipalIdentifierLocal } from '../api/types/PlatformLocal';

/**
 * DEC-RBAC-079/081: resuelve el identificador de login de un tenant (con qué dato inician sesión sus
 * usuarios y si admite un identificador secundario opcional).
 *
 * Lee `platformApi.getTenant(tenantId)` (que devuelve `TenantDetail`) y aplica los defaults de
 * back-compat para tenants viejos (pre-feature) que no traen los campos:
 *   - `principalIdentifier` ausente → `'EMAIL'`
 *   - `allowSecondary` ausente → `false`
 * Con esos defaults el tenant viejo se comporta como hoy (email-only).
 *
 * `enabled: !!tenantId` → no dispara sin un tenant. Mientras carga (o con tenantId vacío) devuelve los
 * defaults, así el consumidor siempre tiene un shape estable con el que construir su schema/UI.
 */
export function useTenantIdentifier(tenantId: string): {
    principalIdentifier: PrincipalIdentifierLocal;
    allowSecondary: boolean;
} {
    const { data } = useQuery({
        queryKey: ['platform', 'tenants', tenantId, 'detail', 'identifier'],
        queryFn: () => platformApi.getTenant(tenantId),
        enabled: !!tenantId,
    });

    return {
        principalIdentifier: data?.principalIdentifier ?? 'EMAIL',
        allowSecondary: data?.allowSecondary ?? false,
    };
}
